import React from 'react';
import { BarChart2, TrendingUp, TrendingDown, MapPin, DollarSign, Users } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Боковая панель с трендами и сравнениями
 */
const TrendingSidebar = ({ results, russianRegions }) => {
  if (!results) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
          Анализ и сравнение
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будет отображаться анализ вашего бюджета по сравнению со средними показателями.
        </p>
      </div>
    );
  }

  // Средние значения для России на 2025 год (для сравнения)
  const benchmarks = {
    averageSalary: 71000, // Средняя зарплата в России
    averageExpenses: 55000, // Средние расходы на семью
    // Распределение расходов в среднем по стране
    averageExpenseBreakdown: {
      housing: 0.30, // 30% на жилье
      food: 0.28, // 28% на продукты
      utilities: 0.10, // 10% на коммунальные услуги
      transportation: 0.10, // 10% на транспорт
      healthcare: 0.05, // 5% на здравоохранение
      other: 0.17 // 17% на прочие расходы
    },
    // Средний процент сбережений
    averageSavingsRate: 0.08, // 8%
  };

  // Корректируем средние значения с учетом региона
  const regionInfo = russianRegions.find(r => r.id === results.basedOn.region) || 
    { name: 'Средний город', costsMultiplier: 1 };
  
  const adjustedBenchmarks = {
    ...benchmarks,
    averageSalary: benchmarks.averageSalary * regionInfo.costsMultiplier,
    averageExpenses: benchmarks.averageExpenses * regionInfo.costsMultiplier,
    // Скорректированный размер семьи
    familySize: results.basedOn.familyMembers + (results.basedOn.hasChildren ? results.basedOn.childrenCount : 0)
  };

  // Расчет отклонений от средних значений
  const deviations = {
    income: calculateDeviation(results.totalIncome, adjustedBenchmarks.averageSalary),
    expenses: calculateDeviation(results.totalExpenses, adjustedBenchmarks.averageExpenses * adjustedBenchmarks.familySize / 2),
    savings: calculateDeviation(results.savingsRate * 100, adjustedBenchmarks.averageSavingsRate * 100)
  };

  // Статистика по регионам с учетом типа семьи
  const regionStatistics = getRegionStatistics(results.basedOn.region, results.basedOn.familyMembers, results.basedOn.hasChildren, russianRegions);

  // Распределение по категориям
  const distributionPercentages = {
    essentials: Math.round(results.essentialRatio * 100),
    nonEssentials: Math.round(results.nonEssentialRatio * 100),
    savings: parseInt(results.savingsPercentage || 10),
    investments: parseInt(results.investmentsPercentage || 10)
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
        Анализ и сравнение
      </h3>

      {/* Информация о регионе */}
      <div className="flex items-start mb-4">
        <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-1.5 mt-0.5" />
        <div>
          <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {regionInfo.name}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {
              adjustedBenchmarks.familySize > 1 
                ? `Семья из ${adjustedBenchmarks.familySize} ${adjustedBenchmarks.familySize >= 5 ? 'человек' : 'человек'}`
                : 'Один человек'
            }
            {results.basedOn.hasChildren && results.basedOn.childrenCount > 0 && 
              ` (${results.basedOn.childrenCount} ${getDeclension(results.basedOn.childrenCount, 'ребенок', 'ребенка', 'детей')})`
            }
          </div>
        </div>
      </div>

      {/* Сравнение со средними показателями */}
      <div className="mb-5 space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Сравнение со средними показателями
        </h4>

        {/* Доход */}
        <div className="bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="flex justify-between mb-1">
            <div className="text-xs text-gray-500 dark:text-gray-400">Доход</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              В регионе: {formatCurrency(adjustedBenchmarks.averageSalary * adjustedBenchmarks.familySize / 2)} ₽
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {formatCurrency(results.totalIncome)} ₽
            </div>
            <ComparisonBadge deviation={deviations.income} isHigherBetter={true} />
          </div>
        </div>

        {/* Расходы */}
        <div className="bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="flex justify-between mb-1">
            <div className="text-xs text-gray-500 dark:text-gray-400">Расходы</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              В регионе: {formatCurrency(adjustedBenchmarks.averageExpenses * adjustedBenchmarks.familySize / 2)} ₽
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {formatCurrency(results.totalExpenses)} ₽
            </div>
            <ComparisonBadge deviation={deviations.expenses} isHigherBetter={false} />
          </div>
        </div>

        {/* Сбережения */}
        <div className="bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="flex justify-between mb-1">
            <div className="text-xs text-gray-500 dark:text-gray-400">Доля сбережений</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              В среднем: {formatNumber(adjustedBenchmarks.averageSavingsRate * 100, 0)}%
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {formatNumber(results.savingsRate * 100, 0)}%
            </div>
            <ComparisonBadge deviation={deviations.savings} isHigherBetter={true} />
          </div>
        </div>
      </div>

      {/* Статистика по регионам */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Статистика по регионам для вашего типа семьи
        </h4>
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 border border-indigo-100 dark:border-indigo-800">
          <div className="space-y-2 text-xs text-indigo-700 dark:text-indigo-300">
            <div className="flex justify-between items-center">
              <span>Средний доход:</span>
              <span className="font-medium">
                {formatCurrency(regionStatistics.averageIncome)} ₽
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>Средние расходы на жилье:</span>
              <span className="font-medium">
                {formatCurrency(regionStatistics.averageHousing)} ₽
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>Средние расходы на продукты:</span>
              <span className="font-medium">
                {formatCurrency(regionStatistics.averageGroceries)} ₽
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>Средние расходы на ЖКХ:</span>
              <span className="font-medium">
                {formatCurrency(regionStatistics.averageUtilities)} ₽
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Сравнение по категориям */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Структура расходов
        </h4>
        <div className="bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="text-xs text-center mb-2 text-gray-500 dark:text-gray-400">
            Идеальное соотношение: 50% нужды, 30% желания, 20% сбережения
          </div>
          <div className="relative h-5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="absolute top-0 left-0 h-full bg-amber-500 dark:bg-amber-600" 
              style={{ width: `${Math.min(100, distributionPercentages.essentials)}%` }}
              title={`Обязательные расходы: ${distributionPercentages.essentials}%`}
            ></div>
            <div 
              className="absolute top-0 h-full bg-red-500 dark:bg-red-600" 
              style={{ 
                left: `${Math.min(100, distributionPercentages.essentials)}%`,
                width: `${Math.min(100 - distributionPercentages.essentials, distributionPercentages.nonEssentials)}%` 
              }}
              title={`Дополнительные расходы: ${distributionPercentages.nonEssentials}%`}
            ></div>
            <div 
              className="absolute top-0 h-full bg-blue-500 dark:bg-blue-600" 
              style={{ 
                left: `${Math.min(100, (distributionPercentages.essentials + distributionPercentages.nonEssentials))}%`,
                width: `${Math.min(
                  100 - (distributionPercentages.essentials + distributionPercentages.nonEssentials),
                  distributionPercentages.savings
                )}%` 
              }}
              title={`Сбережения: ${distributionPercentages.savings}%`}
            ></div>
          </div>
          <div className="mt-2 flex justify-between text-xs text-gray-600 dark:text-gray-400">
            <div>Нужды: {distributionPercentages.essentials}%</div>
            <div>Желания: {distributionPercentages.nonEssentials}%</div>
            <div>Сбережения: {distributionPercentages.savings}%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Компонент бейджа для сравнения с бенчмарками
const ComparisonBadge = ({ deviation, isHigherBetter }) => {
  // Определяем, хорошее ли отклонение (выше среднего для доходов, ниже - для расходов)
  const isGood = isHigherBetter ? deviation > 0 : deviation < 0;
  
  // Если отклонение незначительное (±5%), считаем его нейтральным
  const isNeutral = Math.abs(deviation) <= 5;
  
  let bgColor, textColor, icon;
  
  if (isNeutral) {
    bgColor = 'bg-gray-100 dark:bg-gray-800';
    textColor = 'text-gray-700 dark:text-gray-300';
    icon = null;
  } else if (isGood) {
    bgColor = 'bg-emerald-100 dark:bg-emerald-900';
    textColor = 'text-emerald-700 dark:text-emerald-300';
    icon = <TrendingUp className="w-3 h-3 mr-1" />;
  } else {
    bgColor = 'bg-red-100 dark:bg-red-900';
    textColor = 'text-red-700 dark:text-red-300';
    icon = <TrendingDown className="w-3 h-3 mr-1" />;
  }
  
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor}`}>
      {icon}
      {deviation > 0 ? '+' : ''}{deviation}%
    </span>
  );
};

// Расчет процентного отклонения
const calculateDeviation = (actual, benchmark) => {
  if (benchmark === 0) return 0;
  return Math.round((actual - benchmark) / benchmark * 100);
};

// Получение статистики по регионам
const getRegionStatistics = (regionId, familyMembers, hasChildren, russianRegions) => {
  // Базовые показатели для среднего города (множитель 1.0)
  const baseStats = {
    averageIncome: 35000, // на человека
    averageHousing: 20000, // за квартиру
    averageGroceries: 12000, // на человека
    averageUtilities: 5000, // за квартиру
  };
  
  // Находим множитель для региона
  const region = russianRegions.find(r => r.id === regionId);
  const multiplier = region ? region.costsMultiplier : 1;
  
  // Корректируем статистику с учетом размера семьи и региона
  const familyMultiplier = hasChildren ? familyMembers * 0.9 : familyMembers; // с детьми немного дешевле на человека
  
  return {
    averageIncome: Math.round(baseStats.averageIncome * multiplier * familyMembers),
    averageHousing: Math.round(baseStats.averageHousing * multiplier),
    averageGroceries: Math.round(baseStats.averageGroceries * multiplier * familyMultiplier),
    averageUtilities: Math.round(baseStats.averageUtilities * multiplier * (1 + (familyMembers - 1) * 0.2)), // коммуналка растет не линейно
  };
};

// Склонение существительных
const getDeclension = (number, one, two, many) => {
  const mod10 = number % 10;
  const mod100 = number % 100;
  
  if (mod100 >= 11 && mod100 <= 19) {
    return many;
  }
  
  if (mod10 === 1) {
    return one;
  }
  
  if (mod10 >= 2 && mod10 <= 4) {
    return two;
  }
  
  return many;
};

export default TrendingSidebar;